import React, { useState, useEffect } from 'react';

import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';
import { supabase } from '../supabaseClient';

const SearchPage = ({ searchTerm, onAddToCart, onProductClick }) => {
  const [allProducts, setAllProducts] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});      // { [category_id]: "tecnologia" }
  const [subcatMap, setSubcatMap] = useState({});          // { [subcategory_id]: "cargadores" }
  const [results, setResults] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(10);

  // fallback por si entran directo a /search/xxxx
  const fallbackTerm = window.location.pathname.startsWith('/search/')
    ? decodeURIComponent(window.location.pathname.replace('/search/', '').trim())
    : '';
  const termToSearch =
    searchTerm || (fallbackTerm !== 'search' ? fallbackTerm : '');

  /* =========================
     1. Cargar categorías y subcategorías y productos
     ========================= */
  useEffect(() => {
    const fetchAll = async () => {
      try {
        // 1. categorías (id, slug)
        const { data: cats, error: catErr } = await supabase
          .from('categories')
          .select('id, slug');
        if (catErr) {
          console.error('Error cargando categories:', catErr.message);
        }

        // 2. subcategorías (id, slug)
        const { data: subs, error: subErr } = await supabase
          .from('subcategories')
          .select('id, slug');
        if (subErr) {
          console.error('Error cargando subcategories:', subErr.message);
        }

        // 3. productos
        const { data: prods, error: prodErr } = await supabase
          .from('products')
          .select('*');
        if (prodErr) {
          console.error('Error cargando products:', prodErr.message);
        }

        // construir mapas id -> slug
        const catMap = {};
        (cats || []).forEach((c) => {
          if (c && c.id != null) {
            catMap[c.id] = c.slug;
          }
        });

        const scMap = {};
        (subs || []).forEach((s) => {
          if (s && s.id != null) {
            scMap[s.id] = s.slug;
          }
        });

        setCategoryMap(catMap);
        setSubcatMap(scMap);

        setAllProducts(prods || []);
      } catch (err) {
        console.error('Error inesperado en fetchAll:', err);
      }
    };

    fetchAll();
  }, []);

  /* =========================
     2. Filtrar resultados en base al término de búsqueda
     ========================= */
  useEffect(() => {
    if (termToSearch && termToSearch.trim() !== '') {
      const lowerCaseSearchTerm = termToSearch.trim().toLowerCase();

      const filtered = allProducts.filter((product) => {
        const matchName =
          product.name &&
          product.name.toLowerCase().includes(lowerCaseSearchTerm);

        const matchDescription =
          product.description &&
          product.description.toLowerCase().includes(lowerCaseSearchTerm);

        // SKU exacto
        const matchSku =
          product.sku &&
          product.sku.toLowerCase() === lowerCaseSearchTerm;

        // También dejamos esta: buscar en slug de categoría o subcategoría,
        // pero ahora usando los mapas:
        const catSlug = categoryMap[product.category_id];
        const subSlug = subcatMap[product.subcategory_id];

        const matchCatSlug =
          catSlug && catSlug.toLowerCase().includes(lowerCaseSearchTerm);

        const matchSubSlug =
          subSlug && subSlug.toLowerCase().includes(lowerCaseSearchTerm);

        return (
          matchName ||
          matchDescription ||
          matchSku ||
          matchCatSlug ||
          matchSubSlug
        );
      });

      setResults(filtered);
      setCurrentPage(1);
    } else {
      setResults([]);
    }
  }, [termToSearch, allProducts, categoryMap, subcatMap]);

  /* =========================
     3. Paginación local
     ========================= */
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = results.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  /* =========================
     4. Render
     ========================= */
  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <h5 className="text-1xl sm:text-2xl font-extrabold text-gray-900 mb-6 sm:mb-10 text-center">
        {termToSearch && termToSearch.trim() !== ''
          ? `Mostrando resultados para "${termToSearch}"`
          : 'Ingresa un término para buscar'}
      </h5>

      {termToSearch &&
        termToSearch.trim() !== '' &&
        currentProducts.length > 0 && (
          <div className="flex justify-end items-center mb-6">
            <label
              htmlFor="productsPerPage"
              className="text-gray-700 mr-2"
            >
              Mostrar:
            </label>
            <select
              id="productsPerPage"
              value={productsPerPage}
              onChange={(e) =>
                setProductsPerPage(Number(e.target.value))
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value={results.length}>Todos</option>
            </select>
          </div>
        )}

      {termToSearch && termToSearch.trim() !== '' ? (
        currentProducts.length === 0 ? (
          <p className="text-gray-600 text-lg text-center py-8">
            No se encontraron productos que coincidan con tu búsqueda.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4 sm:gap-6">
            {currentProducts.map((p) => {
              // slugs reales a partir de IDs
              const catSlug = categoryMap[p.category_id];
              const subSlug = subcatMap[p.subcategory_id];

              // construir la ruta base que vamos a pasarle al click
              // /categoria/subcategoria (si hay subcat)
              // /categoria           (si no hay subcat)
              let basePath = '/tecnologia'; // fallback ultra seguro
              if (catSlug && subSlug) {
                basePath = `/${catSlug}/${subSlug}`;
              } else if (catSlug) {
                basePath = `/${catSlug}`;
              }

              return (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAddToCart={onAddToCart}
                  onProductClick={() => {
                    onProductClick(p.id, basePath);
                  }}
                />
              );
            })}
          </div>
        )
      ) : (
        <p className="text-gray-600 text-lg text-center py-8">
          Escribe algo en la barra de búsqueda para encontrar productos.
        </p>
      )}

      {termToSearch &&
        termToSearch.trim() !== '' &&
        results.length > productsPerPage && (
          <Pagination
            productsPerPage={productsPerPage}
            totalProducts={results.length}
            paginate={paginate}
            currentPage={currentPage}
          />
        )}
    </div>
  );
};

export default SearchPage;
